import React, { useState, useRef, forwardRef, useImperativeHandle } from 'react';

interface FileUploadProps {
  isOpen: boolean;
  onClose: () => void;
  onFileUpload: (file: File) => void;
}

interface FileUploadRef {
  triggerFileInput: () => void;
  clearFileInput?: () => void;
}

const FileUpload = forwardRef<FileUploadRef, FileUploadProps>(({ isOpen, onClose, onFileUpload }, ref) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const clearFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }
  
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    setError(null);
    
    if (files && files.length > 0) {
      const file = files[0];
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      
      // Check if file type is allowed (CSV or Excel)
      if (fileExtension === 'csv' || 
          fileExtension === 'xlsx' || 
          fileExtension === 'xls' || 
          file.type === 'text/csv' || 
          file.type === 'application/vnd.ms-excel' || 
          file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
        
        onFileUpload(file);
        onClose();
      } else {
        setError('Only CSV and Excel files are allowed');
        // Reset the input
        clearFileInput();
      }
    }
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Expose the triggerFileInput function to parent components
  useImperativeHandle(ref, () => ({
    triggerFileInput,
    clearFileInput
  }));

  // Clear error when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setError(null);
    }
  }, [isOpen]);
  
  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
        onClick={(e) => e.stopPropagation()}
        accept=".csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
      />
      {error && <div style={{ color: 'red' }}>{error}</div>}
    </>
  );
});

FileUpload.displayName = 'FileUpload';

export default FileUpload;